import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode, useRef } from 'react';
import { OktaAuth, AuthState, UserClaims } from '@okta/okta-auth-js';
import { oktaConfig, validateOktaConfig } from '../config/auth.config';
import { TokenRefreshService } from '../services/tokenRefreshService';
import { SessionService, SessionInfo } from '../services/sessionService';

// Types
export interface AuthContextType {
  authState: AuthState | null;
  oktaAuth: OktaAuth | null;
  user: UserClaims | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userNotAssigned: boolean;
  sessionInfo: SessionInfo | null;
  login: (originalUri?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | undefined>;
  refreshTokens: () => Promise<boolean>;
  extendSession: () => Promise<boolean>;
  getSessionStats: () => any;
}

interface AuthProviderProps {
  children: ReactNode;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [oktaAuth, setOktaAuth] = useState<OktaAuth | null>(null);
  const [user, setUser] = useState<UserClaims | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [userNotAssigned, setUserNotAssigned] = useState(false);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  
  // Service instances
  const tokenRefreshServiceRef = useRef<TokenRefreshService | null>(null);
  const sessionServiceRef = useRef<SessionService | null>(null);

  // Initialize services when oktaAuth is available
  useEffect(() => {
    if (oktaAuth && !tokenRefreshServiceRef.current) {
      tokenRefreshServiceRef.current = new TokenRefreshService(oktaAuth);
      
      // Start immediately if user is already authenticated
      if (authState?.isAuthenticated) {
        tokenRefreshServiceRef.current.startAutoRefresh();
      }
    }

    if (oktaAuth && !sessionServiceRef.current) {
      sessionServiceRef.current = new SessionService(oktaAuth);
      
      // Start immediately if user is already authenticated
      if (authState?.isAuthenticated) {
        sessionServiceRef.current.startSessionMonitoring();
      }
    }
  }, [oktaAuth, authState?.isAuthenticated]);

  // Set up event listeners for service events
  useEffect(() => {
    const handleTokenRefreshFailed = (event: CustomEvent) => {
      setError('Session expired. Please log in again.');
      if (oktaAuth) {
        oktaAuth.signOut().catch(console.error);
      }
    };

    const handleSessionExpired = (event: CustomEvent) => {
      setError('Your session has expired. Please log in again.');
      if (oktaAuth) {
        oktaAuth.signOut().catch(console.error);
      }
    };

    window.addEventListener('tokenRefreshFailed', handleTokenRefreshFailed as EventListener);
    window.addEventListener('sessionExpired', handleSessionExpired as EventListener);

    return () => {
      window.removeEventListener('tokenRefreshFailed', handleTokenRefreshFailed as EventListener);
      window.removeEventListener('sessionExpired', handleSessionExpired as EventListener);
    };
  }, [oktaAuth]);

  // Update session info periodically
  useEffect(() => {
    if (!sessionServiceRef.current || !authState?.isAuthenticated) {
      setSessionInfo(null);
      return;
    }

    const updateSessionInfo = () => {
      if (sessionServiceRef.current) {
        const info = sessionServiceRef.current.getSessionInfo();
        setSessionInfo(info);
      }
    };

    // Update immediately
    updateSessionInfo();

    // Update every 30 seconds
    const interval = setInterval(updateSessionInfo, 30000);

    return () => clearInterval(interval);
  }, [authState?.isAuthenticated]);

  // Initialize Okta Auth - only once
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        console.log('Initializing Okta Auth...');
        
        // Validate configuration
        if (!validateOktaConfig(oktaConfig)) {
          throw new Error('Invalid Okta configuration. Please check your environment variables.');
        }

        // Create OktaAuth instance
        const authInstance = new OktaAuth(oktaConfig);
        
        if (!isMounted) return;
        
        setOktaAuth(authInstance);

        // Check if we're on the callback URL
        const isCallbackUrl = window.location.pathname === '/authentication/login-callback';
        const urlParams = new URLSearchParams(window.location.search);
        const hasCallbackParams = urlParams.has('code') || urlParams.has('state') || urlParams.has('error');
        
        if (isCallbackUrl && hasCallbackParams) {
          console.log('Detected login callback with parameters, processing...');
          
          // Check for specific OAuth error indicating user not assigned
          const error = urlParams.get('error');
          const errorDescription = urlParams.get('error_description');
          
          if (error === 'access_denied' && errorDescription && errorDescription.includes('not assigned')) {
            console.log('User is not assigned to the application, setting flag and redirecting');
            setUserNotAssigned(true);
            setIsLoading(false);
            // Clear any problematic storage and redirect to home
            localStorage.removeItem('okta-token-storage');
            sessionStorage.removeItem('okta-shared-transaction-storage');
            window.history.replaceState({}, '', '/');
            return;
          }
          
          try {
            await authInstance.handleLoginRedirect();
          } catch (callbackError: any) {
            console.error('Login callback failed:', callbackError);
            
            // Handle specific user assignment errors
            if (callbackError.name === 'OAuthError' && callbackError.message?.includes('not assigned')) {
              console.log('User not assigned error, setting flag and redirecting');
              setUserNotAssigned(true);
              setIsLoading(false);
              localStorage.removeItem('okta-token-storage');
              sessionStorage.removeItem('okta-shared-transaction-storage');
              window.history.replaceState({}, '', '/');
              return;
            }
            
            // Handle token parsing errors - but be more careful about when to set userNotAssigned
            if (callbackError.name === 'AuthSdkError' && callbackError.message?.includes('Unable to parse a token')) {
              console.log('Token parsing failed, clearing session and redirecting');
              localStorage.removeItem('okta-token-storage');
              sessionStorage.removeItem('okta-shared-transaction-storage');
              window.history.replaceState({}, '', '/');
              return;
            }
            
            // For other errors, show error message
            setError('Authentication failed. Please try again.');
            window.history.replaceState({}, '', '/');
          }
        } else if (isCallbackUrl && !hasCallbackParams) {
          console.log('Callback URL detected but no callback parameters, redirecting to home...');
          window.history.replaceState({}, '', '/');
        }

        // Set up auth state listener
        authInstance.authStateManager.subscribe((newAuthState: AuthState) => {
          if (!isMounted) return;
          
          console.log('Auth state updated:', {
            isAuthenticated: newAuthState.isAuthenticated,
            isPending: newAuthState.isPending
          });
          
          setAuthState(newAuthState);
          setIsLoading(false);
          
          if (newAuthState.isAuthenticated && newAuthState.idToken) {
            setUser(newAuthState.idToken.claims);
            setError(null);
            setUserNotAssigned(false); // Clear the flag on successful auth
            
            // Start session and token refresh services
            if (tokenRefreshServiceRef.current) {
              tokenRefreshServiceRef.current.startAutoRefresh();
            }
            
            if (sessionServiceRef.current) {
              sessionServiceRef.current.startSessionMonitoring();
            }
            
            // Handle post-authentication redirect only if we processed a callback
            if (isCallbackUrl && hasCallbackParams) {
              const originalUri = sessionStorage.getItem('okta-original-uri') || '/';
              sessionStorage.removeItem('okta-original-uri');
              console.log('Authentication successful, redirecting to:', originalUri);
              window.history.replaceState({}, '', originalUri);
            }
          } else {
            setUser(null);
            
            // Stop session and token refresh services
            if (tokenRefreshServiceRef.current) {
              tokenRefreshServiceRef.current.stopAutoRefresh();
            }
            
            if (sessionServiceRef.current) {
              sessionServiceRef.current.stopSessionMonitoring();
            }
            
            // Clear storage when not authenticated
            if (!newAuthState.isPending) {
              localStorage.removeItem('okta-token-storage');
              sessionStorage.removeItem('okta-shared-transaction-storage');
            }
          }
        });

        // Start the auth state manager
        await authInstance.start();
        
        if (!isMounted) return;
        
        setInitialized(true);
        console.log('Okta Auth initialized successfully');

      } catch (err) {
        console.error('Failed to initialize Okta Auth:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to initialize authentication');
          setIsLoading(false);
        }
      }
    };

    if (!initialized) {
      initializeAuth();
    }

    return () => {
      isMounted = false;
    };
  }, [initialized]);

  // Login function
  const login = useCallback(async (originalUri?: string) => {
    if (!oktaAuth) {
      throw new Error('Okta Auth not initialized');
    }

    try {
      setError(null);
      setUserNotAssigned(false); // Clear the flag when attempting login
      
      // Store original URI for redirect after login
      if (originalUri) {
        sessionStorage.setItem('okta-original-uri', originalUri);
      }

      console.log('Starting login redirect...');
      await oktaAuth.signInWithRedirect({
        originalUri: originalUri || window.location.pathname
      });
      
    } catch (err) {
      console.error('Login failed:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    }
  }, [oktaAuth]);

  // Logout function
  const logout = useCallback(async () => {
    if (!oktaAuth) {
      throw new Error('Okta Auth not initialized');
    }

    try {
      setError(null);
      setUserNotAssigned(false); // Clear the flag on logout
      console.log('Starting logout process...');
      
      // First, sign out from Okta (this will redirect to Okta and then back to postLogoutRedirectUri)
      await oktaAuth.signOut({
        postLogoutRedirectUri: oktaConfig.postLogoutRedirectUri
      });
      
    } catch (err) {
      console.error('Logout failed:', err);
      setError(err instanceof Error ? err.message : 'Logout failed');
      
      // Force clear everything and reload as fallback
      console.log('Logout failed, clearing storage and redirecting...');
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = oktaConfig.postLogoutRedirectUri;
      
      throw err;
    }
  }, [oktaAuth]);

  // Get access token with enhanced refresh logic
  const getAccessToken = useCallback(async (): Promise<string | undefined> => {
    if (!oktaAuth || !authState?.isAuthenticated) {
      return undefined;
    }

    try {
      let accessToken = oktaAuth.getAccessToken();
      
      // If no token or token refresh service indicates refresh is needed
      if (!accessToken || (tokenRefreshServiceRef.current && await tokenRefreshServiceRef.current.refreshTokensIfNeeded())) {
        // Try to get the token again after refresh
        accessToken = oktaAuth.getAccessToken();
      }
      
      return accessToken;
    } catch (err) {
      console.error('Failed to get access token:', err);
      return undefined;
    }
  }, [oktaAuth, authState?.isAuthenticated]);

  // Manually refresh tokens
  const refreshTokens = useCallback(async (): Promise<boolean> => {
    if (!tokenRefreshServiceRef.current) {
      console.warn('Token refresh service not available');
      return false;
    }

    try {
      return await tokenRefreshServiceRef.current.refreshTokensIfNeeded();
    } catch (error) {
      console.error('Failed to refresh tokens:', error);
      return false;
    }
  }, []);

  // Extend session
  const extendSession = useCallback(async (): Promise<boolean> => {
    if (!sessionServiceRef.current) {
      console.warn('Session service not available');
      return false;
    }

    try {
      return await sessionServiceRef.current.extendSession();
    } catch (error) {
      console.error('Failed to extend session:', error);
      return false;
    }
  }, []);

  // Get session statistics
  const getSessionStats = useCallback(() => {
    if (!sessionServiceRef.current) {
      return null;
    }

    return sessionServiceRef.current.getSessionStats();
  }, []);

  const contextValue: AuthContextType = {
    authState,
    oktaAuth,
    user,
    isAuthenticated: authState?.isAuthenticated || false,
    isLoading,
    error,
    userNotAssigned,
    sessionInfo,
    login,
    logout,
    getAccessToken,
    refreshTokens,
    extendSession,
    getSessionStats
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;